import React from 'react';

import {
  Add as AddIcon,
  BorderColor as BorderColorIcon,
  Redo as RedoIcon,
} from '@mui/icons-material';
import { Button } from '@mui/material';

import { STATUSES_FILTER_OPTIONS } from '@clearverify/constants';
import { formatPhoneNumber, parseNullableValue } from '@clearverify/utils';

import { type PeoStaffInfoFragment, UserStatus, useResendInviteToPeoStaffMutation } from 'src/api';
import { ClickableLabel, UserStatusChip } from 'src/components/common';
import type { TableColumn } from 'src/components/table';
import { Table, TableFilter, TableRowAction, TableTopAction } from 'src/components/table';
import { ModalId } from 'src/constants/modals';
import { PEO_STAFF_ROLE_LABEL_MAP } from 'src/constants/peo';
import { useNotification } from 'src/hooks/app';
import { useScreenInfo, useSearch } from 'src/hooks/common';
import { useModal } from 'src/hooks/shared';
import { ResendInviteModalArgs } from 'src/modals/invite';
import { PeoStaffViewDetailsModalArgs } from 'src/modals/peoStaff/PeoStaffViewDetailsModal/types';
import { PaginationContextValue } from 'src/providers/PaginationProvider';

type PeoStaffTablePeoAdminProps = {
  peoStaffList: PeoStaffInfoFragment[];
  peoStaffCount: number;
  onRefresh?: () => Promise<void>;
  pagination: PaginationContextValue;
  loading: boolean;
};

export const PeoStaffTablePeoAdmin: React.FC<PeoStaffTablePeoAdminProps> = ({
  peoStaffList,
  peoStaffCount,
  onRefresh,
  pagination,
  loading,
}) => {
  const { openModal } = useModal();
  const { setSearchValue } = useSearch();
  const { isMobile } = useScreenInfo();
  const { createApolloContext } = useNotification();

  const [resendInviteToPeoStaff] = useResendInviteToPeoStaffMutation({
    context: createApolloContext({
      successMessage: 'Invite has been resent successfully',
    }),
  });

  const rowActions = React.useMemo(() => {
    const actions: Array<TableRowAction<PeoStaffInfoFragment>> = [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: peoStaff => {
          openModal(ModalId.PeoStaffViewDetails, {
            peoStaffId: peoStaff.id,
          } satisfies PeoStaffViewDetailsModalArgs);
        },
      },
      {
        startIcon: <RedoIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'Resend Invite',
        hidden: staff => staff.user.status !== UserStatus.Pending,
        onAction: staff => {
          const args: ResendInviteModalArgs = {
            toStaff: 'company',
            staffEmail: staff.user.email,
            onSubmit: async () => {
              const { data } = await resendInviteToPeoStaff({
                variables: { peoStaffId: staff.id },
              });
              return data?.resendInviteToPeoStaff;
            },
          };

          openModal(ModalId.ResendInvite, args);
        },
      },
    ];
    return actions;
  }, [openModal, resendInviteToPeoStaff]);

  const topActions = React.useMemo(
    (): Array<TableTopAction> => [
      {
        actionName: 'Add',
        onAction: () => openModal(ModalId.InvitePeoStaffByPeoAdminModal),
        icon: <AddIcon />,
        show: true,
        render() {
          return (
            <Button
              key={this.actionName}
              variant="contained"
              startIcon={this.icon}
              onClick={this.onAction}
              size={isMobile ? 'large' : 'medium'}
            >
              New
            </Button>
          );
        },
      },
    ],
    [openModal, isMobile],
  );

  return (
    <Table
      dataSource={peoStaffList}
      rowCount={peoStaffCount}
      columns={columns}
      rowActions={rowActions}
      topActions={topActions}
      onSearch={setSearchValue}
      pagination={pagination}
      filters={filters}
      onRefresh={onRefresh}
      loading={loading}
    />
  );
};

const filters: TableFilter[] = [
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: STATUSES_FILTER_OPTIONS,
    },
  },
];

const columns: Array<TableColumn<PeoStaffInfoFragment>> = [
  {
    title: 'Full Name',
    key: 'user.firstName',
    sortable: true,
    render: (_valueByDataPath, peoStaff, { rowActions }) => {
      return (
        <ClickableLabel
          type="button"
          onClick={() => rowActions?.[0]?.onAction(peoStaff)}
          label={peoStaff.user.fullName}
          labelPostfix={PEO_STAFF_ROLE_LABEL_MAP[peoStaff.role]}
          logoUrl={peoStaff.user.avatar?.downloadUrl}
        />
      );
    },
  },
  {
    title: 'Email',
    key: 'user.email',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, peoStaff) => {
      return parseNullableValue(peoStaff.user.email);
    },
  },
  {
    title: 'Phone Number',
    key: 'user.phoneNumber',
    render: (_valueByDataPath, peoStaff) => {
      return parseNullableValue(formatPhoneNumber(peoStaff.user.phoneNumber));
    },
  },
  {
    title: 'Status',
    key: 'user.status',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, peoStaff) => {
      return <UserStatusChip userStatus={peoStaff.user.status} />;
    },
  },
];
